import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'
import { isLoggedInAtom } from '@root/entities/auth'
import { useWebSignIn } from '@root/features/auth/web-sign-in'
import { openTradeSettingsDialogEvent } from '@root/features/trade/trade-settings'
import {
  hideTradingAuditAction,
  tradingAuditVisibilityAtom,
  tradingPairStateAtom,
} from '@root/features/trade/trading'
import {
  reatomBuyControls,
  reatomSellControls,
} from '@root/features/trade/trading-controls/atoms/common'
import { TradingControls } from '@root/features/trade/trading-controls/ui/mobile'
import { Screen } from '@root/shared/layout'
import { InfoMessage } from '@root/shared/ui-kit'
import { tradingNavigationAtom } from '@root/widgets/layout/mobile/atoms'

import { AnalyticsView } from './analytics-view/analytics-view'
import { OrdersView } from './orders-view/orders-view'
import { PositionsView } from './positions-view/positions-view'
import { TradeAuditDrawer } from './trading-audit-drawer'
import { TradingDrawers } from './trading-drawers'
import { TradingPairView } from './trading-pair-view/trading-pair-view'

type TradingScreenProps = {}

export const TradingScreen: React.FC<TradingScreenProps> = () => {
  const [pairState] = useAtom(tradingPairStateAtom)
  const pair = pairState.value
  const [{ open: showAudit }] = useAtom(tradingAuditVisibilityAtom)
  const hiderAudit = useAction(hideTradingAuditAction)
  const [tab] = useAtom(tradingNavigationAtom)
  const haptics = useHapticFeedback()
  const openTradeSettings = useAction(openTradeSettingsDialogEvent)
  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const signIn = useWebSignIn()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const buyControlsModel = useMemo(() => reatomBuyControls(), [])
  // eslint-disable-next-line @reatom/unit-naming-rule
  const sellControlsModel = useMemo(() => reatomSellControls(), [])

  const handleSettingsOpen = () => {
    if (isLoggedIn) {
      haptics.impactOccurred('light')
      openTradeSettings({})
      return
    }
    signIn()
  }

  const renderContent = () => {
    if (AsyncStateN.isError(pairState)) {
      return (
        <ErrorContainer>
          <InfoMessage intent="error" />
        </ErrorContainer>
      )
    }

    if (tab && ['analytics', 'orders', 'positions'].includes(tab)) {
      return (
        <Content>
          <TradingPairView
            buyControlsModel={buyControlsModel}
            sellControlsModel={sellControlsModel}
            chartVariant="minimal"
          />
          {tab === 'analytics' && <AnalyticsView pair={pair} />}
          {tab === 'orders' && <OrdersView pair={pair} />}
          {tab === 'positions' && <PositionsView />}
        </Content>
      )
    }

    return (
      <Content>
        <TradingPairView
          buyControlsModel={buyControlsModel}
          sellControlsModel={sellControlsModel}
          chartVariant="full"
        />
        <TradingControls
          buyControlsModel={buyControlsModel}
          sellControlsModel={sellControlsModel}
          onSettingsOpen={handleSettingsOpen}
        />
      </Content>
    )
  }

  return (
    <Root>
      {renderContent()}

      {isLoggedIn ? <TradingDrawers /> : undefined}
      {pair ? (
        <TradeAuditDrawer
          pair={pair}
          open={showAudit}
          onOpenChange={(open) => {
            if (!open) {
              hiderAudit()
            }
          }}
        />
      ) : undefined}
    </Root>
  )
}

const Root = styled(Screen)`
  background: var(--background-1);
  display: grid;

  overflow-y: auto;
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-columns: minmax(0, auto);

  align-content: start;
  padding-bottom: calc(var(--safe-area-inset-bottom) + 53px);
`

const ErrorContainer = styled.div`
  display: grid;
  padding-top: 32px;
`
